import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useRouter } from 'expo-router';
import { CustomEventNames } from 'features/analytics';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { MinimalCartEntryType } from 'features/cart/types';
import { logOfferEvent } from 'features/offer/utils/analytics';
import {
  parseOfferListPosition,
  toAmplitudeOfferListPosition,
} from 'features/offer/utils/analytics';
import { isEngineReward, rewardToLoyaltyReward } from 'features/reward/utils';
import { logRewardEvent } from 'features/reward/utils/analytics';
import { useStoreContext } from 'features/store';

import { IncentiveDebugTool } from 'components/debug-loyalty/incentive-debug';
import withAddIncentiveToCart from 'components/with-add-incentive-to-cart';
import { LogUserErrorSeverity, logUserErrorMessage } from 'hooks/log-user-error-message';
import { useRoute } from 'hooks/navigation/use-route';
import { useCart } from 'hooks/use-cart';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { removeAppliedRewardsInStorage } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import {
  ICartEntryType,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import { isLoyaltyOffer, isReward, isValidOfferIncentive } from 'state/loyalty/types';
import { useOrderContext } from 'state/order';
import { routes } from 'utils/routing';

import { SHOULD_SELECT_STORE } from '../constants';
import { parseIncentiveData } from '../incentive-card/parse-incentive-data';
import { useLoyaltyIncentivesAvailability } from '../use-loyalty-incentives-availability';

import { ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA } from './constants';
import { IncentiveDetailsView } from './incentive-details.view';
import { IIncentiveDetailsProps, IIncentiveDetailsViewProps } from './types';

/**
 *
 * IncentiveDetails displays an expanded view of a incentive when selected from the incentive list
 * // TODO refactor this component is used for 2 different uses displaying an offer and a reward
 *
 */
const IncentiveDetails = ({
  selectedIncentive,
  engineIncentivesMap,
  addIncentiveToCart,
  showUnavailableDefaultMessage,
  onDismiss,
}: IIncentiveDetailsProps) => {
  const router = useRouter();
  const incentiveLoyaltyEngineId = isEngineReward(selectedIncentive)
    ? selectedIncentive.id
    : selectedIncentive?.loyaltyEngineId || '';
  const selectedIncentiveId = isEngineReward(selectedIncentive)
    ? selectedIncentive.cmsId ?? selectedIncentive.id
    : selectedIncentive?._id || '';
  const engineReward =
    engineIncentivesMap[incentiveLoyaltyEngineId] || engineIncentivesMap[selectedIncentiveId];
  const isRewardIncentive = isEngineReward(selectedIncentive) || isReward(selectedIncentive);

  const pointCost = isEngineReward(selectedIncentive)
    ? selectedIncentive.pointCost
    : engineReward?.pointCost;
  const locked = isEngineReward(selectedIncentive)
    ? selectedIncentive.locked
    : engineReward?.locked;
  const redemptionPercentage = isEngineReward(selectedIncentive)
    ? selectedIncentive.redemptionPercentage
    : engineReward?.redemptionPercentage;
  const remainingPointsNeededForRedemption = isEngineReward(selectedIncentive)
    ? selectedIncentive.remainingPointsNeededForRedemption
    : engineReward?.remainingPointsNeededForRedemption;
  const rewardBenefitId = isEngineReward(selectedIncentive)
    ? selectedIncentive.rewardBenefitId
    : engineReward?.rewardBenefitId;

  const {
    recent: { setPendingRecentItem },
  } = useOrderContext();

  const { params } = useRoute<{
    [ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA]: string;
    olp?: string;
  }>();
  const { removeAllFromCart, cartEntries } = useCart();
  const {
    inRestaurantLoyaltyEnabledAtRestaurant,
    clearInRestaurantRedemptionAllRewards,
    enableRewardRedemption,
    inRestaurantRedemptionCart,
  } = useInRestaurantRedemptionContext();
  const rewardData = isEngineReward(selectedIncentive)
    ? parseIncentiveData(selectedIncentive)
    : parseIncentiveData(selectedIncentive);
  // Reward eligibility is service-owned; consume its result instead of recreating its rules.
  const hasServiceRewardErrors =
    isEngineReward(selectedIncentive) && Boolean(selectedIncentive.errors?.length);

  const [checkingAvailability, setCheckingAvailability] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const { offersCooldown, rewardsCooldown } = useLoyaltyContext();
  const cooldownInfo = rewardData.isOffer ? offersCooldown : rewardsCooldown;

  const isDebugToolEnabled = useFlag(LaunchDarklyFlag.ENABLE_DEBUG_TOOL);
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const enableMinimalCartUi = useMinimalCartUi();
  const { cartEntries: minimalCartEntries } = useMinimalCart();

  const { incentiveId, incentiveType } =
    useMemo(() => {
      const incentives = isEngineReward(selectedIncentive)
        ? (selectedIncentive.incentives as Array<{ _id?: string; _type?: string } | null> | null) ??
          undefined
        : selectedIncentive.incentives;
      const incentive = isRewardIncentive
        ? incentives?.find(
            benefit => isValidOfferIncentive(benefit) && benefit._id === rewardBenefitId
          )
        : incentives?.[0];
      return {
        incentiveId: isValidOfferIncentive(incentive) ? incentive._id : '',
        incentiveType:
          incentive && typeof incentive === 'object' && '_type' in incentive
            ? (incentive as { _type?: string })._type
            : undefined,
      };
    }, [isRewardIncentive, rewardBenefitId, selectedIncentive]) || {};
  const dispatch = useAppDispatch();
  const { loyaltyUser } = useLoyaltyUser();
  const appliedLoyaltyRewardsArray = useAppSelector(
    selectors.loyalty.selectAppliedLoyaltyRewardsArray
  );
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);

  const {
    checkIncentiveAvailability,
    incentiveUnavailableMessage,
  } = useLoyaltyIncentivesAvailability(
    selectedIncentive,
    pointCost,
    locked,
    isRewardIncentive ? { ignoreDayPart: true } : undefined
  );
  const exceedsBalanceError = t('exceedsRewardsBalance');

  const { noStoreSelected } = useStoreContext();
  // Reset availability state when incentive changes to avoid stale logs/UI.
  useEffect(() => {
    if (!selectedIncentiveId) {
      setCheckingAvailability(false);
      setErrorMessage('');
      return;
    }

    setCheckingAvailability(true);
  }, [selectedIncentiveId]);

  useEffect(() => {
    if (showUnavailableDefaultMessage && isLoyaltyOffer(selectedIncentive)) {
      setErrorMessage(t('incentiveFeedbackInvalidStore', { incentiveType: t('offer') }));
      setCheckingAvailability(false);
      return;
    }
    if (incentiveUnavailableMessage) {
      setErrorMessage(incentiveUnavailableMessage);
      setCheckingAvailability(false);
      return;
    }
    if (noStoreSelected) {
      setErrorMessage('');
      setCheckingAvailability(false);
      return;
    }

    // Check incentive item availability in selected store
    (async () => {
      setErrorMessage('');
      try {
        const id = isUnifiedMenuEnabled ? selectedIncentiveId : `${incentiveType}-${incentiveId}`;
        const isAvailable = await checkIncentiveAvailability(
          id,
          isRewardIncentive ? { ignoreDayPart: true } : undefined
        );
        const storeError = isAvailable ? '' : t('nonAvailableItemInStore');

        setErrorMessage(storeError);
        setCheckingAvailability(false);

        if (storeError) {
          logUserErrorMessage({
            message: storeError,
            severity: LogUserErrorSeverity.Recoverable,
            context: {
              incentiveType,
              incentiveId,
              incentiveLoyaltyEngineId,
              displayName: isEngineReward(selectedIncentive)
                ? selectedIncentive.name
                : (selectedIncentive as any)?.name?.localeRaw?.[0]?.children?.[0]?.text,
            },
          });
        }
      } catch {
        setCheckingAvailability(false);
      }
    })();
  }, [
    checkIncentiveAvailability,
    incentiveId,
    incentiveLoyaltyEngineId,
    incentiveType,
    incentiveUnavailableMessage,
    isRewardIncentive,
    isUnifiedMenuEnabled,
    noStoreSelected,
    selectedIncentive,
    selectedIncentiveId,
    showUnavailableDefaultMessage,
  ]);

  const offerListPosition = parseOfferListPosition(params.olp);

  const logIncentiveDetails = useCallback(
    (isAvailable: boolean, listPosition: number | null) => {
      if (!selectedIncentive) {
        return;
      }

      // Log Reward event
      if (!isLoyaltyOffer(selectedIncentive)) {
        logRewardEvent(
          {
            name: CustomEventNames.REWARD_DETAILS_VIEWED,
            incentives: [
              isEngineReward(selectedIncentive)
                ? rewardToLoyaltyReward(selectedIncentive)
                : selectedIncentive,
            ],
          },
          { skipLoggingToBraze: true }
        );

        return;
      }

      // Log Offer event
      logOfferEvent({
        name: CustomEventNames.OFFER_DETAILS_VIEWED,
        incentives: [selectedIncentive],
        attributes: {
          isAvailable,
          offerListPosition: toAmplitudeOfferListPosition(listPosition),
        },
      });
    },
    [selectedIncentive]
  );

  const incentiveEvaluationResults = offersFeedbackMap[incentiveLoyaltyEngineId];
  const handleRemoveRewardsPress = () => {
    if (appliedLoyaltyRewardsArray?.length) {
      const cartIds = appliedLoyaltyRewardsArray.map(reward => reward.cartId);
      const cartEntriesToRemove = cartEntries.filter(entry => cartIds.includes(entry.cartId));

      // Batch remove all cart entries in a single dispatch
      // removeAllFromCart will also handle removing applied rewards via removeRewardIfNeeded
      if (cartEntriesToRemove.length > 0) {
        removeAllFromCart(cartEntriesToRemove);
      }

      clearInRestaurantRedemptionAllRewards();
      removeAppliedRewardsInStorage();

      // Recalculate stagedCartPoints to ensure it's in sync with user's actual points
      // and remaining applied rewards (handles edge cases where points might have changed)
      dispatch(
        actions.loyalty.resetLoyaltyRewardsState({
          points: loyaltyUser?.points ?? 0,
          shouldResetAvailableRewardsMap: false,
        })
      );
    }
  };

  const onlyShowRedeemRewardInRestaurantCTA =
    String(params[ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA]) === 'true';

  const canAddToInRestaurantOrder =
    (!SHOULD_SELECT_STORE || inRestaurantLoyaltyEnabledAtRestaurant) &&
    ((isLoyaltyOffer(selectedIncentive) && !selectedIncentive.mobileOrderOnly) ||
      (isRewardIncentive && enableRewardRedemption && !selectedIncentive.mobileOrderOnly));

  const canAddToMobileOrder =
    !onlyShowRedeemRewardInRestaurantCTA &&
    (isLoyaltyOffer(selectedIncentive) || isRewardIncentive);

  const isAlreadyAppliedToMobileOrder =
    isLoyaltyOffer(selectedIncentive) &&
    (appliedOffers.some(offer => offer.id === selectedIncentive.loyaltyEngineId) ||
      (enableMinimalCartUi &&
        minimalCartEntries.some(
          entry =>
            (entry.type === MinimalCartEntryType.discount ||
              entry.type === MinimalCartEntryType.incentive) &&
            entry.referenceId === selectedIncentive.loyaltyEngineId
        )));

  const isAlreadyAppliedToInRestaurantOrder =
    isLoyaltyOffer(selectedIncentive) &&
    inRestaurantRedemptionCart.some(
      entry =>
        entry.type === ICartEntryType.OFFER &&
        (entry.details.offer._id === selectedIncentive._id ||
          entry.details.offer.loyaltyEngineId === selectedIncentive.loyaltyEngineId)
    );

  const incentiveViewProps = {
    ...rewardData,
    pointCost,
    locked,
    redemptionPercentage,
    handleRedeemRewardPress: () => {
      // First force reset pending recent items to prevent adding multiple items to the cart,
      // Then add the incentive to the cart
      setPendingRecentItem(null);
      addIncentiveToCart({
        incentive: selectedIncentive,
        method: RewardRedemptionMethod.MOBILE,
      });
    },
    handleInRestaurantRedemptionPress: () => {
      addIncentiveToCart({
        incentive: selectedIncentive,
        method: RewardRedemptionMethod.IN_RESTAURANT,
      });
    },
    handleViewInCartPress: () => {
      router.replace(routes.cart);
    },
    handleShowCodeToRedeemPress: () => {
      router.replace(routes.myCode);
    },
    remainingPointsNeededForRedemption,
    errorMessage,
    checkingAvailability,
    hasServiceRewardErrors,
    canAddToInRestaurantOrder,
    canAddToMobileOrder,
    isAlreadyAppliedToInRestaurantOrder,
    isAlreadyAppliedToMobileOrder,
    showRemoveRewardButton: incentiveUnavailableMessage === exceedsBalanceError,
    handleRemoveRewardsPress,
    incentiveEvaluationResults,
    cooldown: cooldownInfo,
    onLogIncentiveDetailsEvent: logIncentiveDetails,
    offerListPosition,
    onDismiss,
  } as IIncentiveDetailsViewProps;

  return (
    <>
      {isDebugToolEnabled && (
        <IncentiveDebugTool
          incentive={
            isEngineReward(selectedIncentive)
              ? rewardToLoyaltyReward(selectedIncentive)
              : selectedIncentive
          }
        />
      )}
      <IncentiveDetailsView {...incentiveViewProps} />
    </>
  );
};

export default memo(withAddIncentiveToCart(IncentiveDetails));
